import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { api } from '@/api';
import { toast } from 'sonner';
import { 
  FileText, CheckCircle, AlertCircle, Clock, Send, 
  Loader2, Building2, MapPin, Calendar
} from 'lucide-react';

const COMMENT_TAGS = [
  'Time Impact', 'Cost Impact', 'Scope Change', 'Quality Concern',
  'Risk', 'Commercial', 'Information Needed', 'Other'
];

export default function PublicSIComment() {
  const { tokenId } = useParams();
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState(null);
  const [siData, setSiData] = useState(null);
  
  const [formData, setFormData] = useState({
    comment_text: '',
    tags: [],
    cost_impact: '',
    time_impact: '',
    recommended_action: ''
  });

  useEffect(() => {
    loadSIDetails();
  }, [tokenId]);

  const loadSIDetails = async () => {
    try {
      const response = await api.publicSIComment.getSIDetails(tokenId);
      setSiData(response.data);
    } catch (err) {
      console.error('Error loading SI:', err);
      if (err.response?.status === 410) {
        setError('This link has expired. Please request a new link from your team.');
      } else if (err.response?.status === 404) {
        setError('Invalid link. Please check the URL or request a new link.');
      } else {
        setError('Unable to load the Site Instruction. Please try again later.');
      }
    } finally {
      setLoading(false);
    }
  };

  const toggleTag = (tag) => {
    if (formData.tags.includes(tag)) {
      setFormData({ ...formData, tags: formData.tags.filter(t => t !== tag) });
    } else {
      setFormData({ ...formData, tags: [...formData.tags, tag] });
    }
  };

  const handleSubmit = async () => {
    if (!formData.comment_text.trim()) {
      toast.error('Please enter your comment');
      return;
    }

    setSubmitting(true);
    try {
      await api.publicSIComment.submitComment(tokenId, formData);
      setSubmitted(true);
      toast.success('Comment submitted successfully!');
    } catch (err) {
      console.error('Error submitting comment:', err);
      if (err.response?.status === 410) {
        setError('This link has expired. Please request a new link from your team.');
      } else {
        toast.error('Failed to submit comment. Please try again.');
      }
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="h-8 w-8 animate-spin text-slate-600 mx-auto mb-4" />
          <p className="text-slate-600">Loading Site Instruction...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center p-4">
        <div className="max-w-md w-full bg-white rounded-lg shadow-lg p-8 text-center">
          <AlertCircle className="h-16 w-16 text-red-500 mx-auto mb-4" />
          <h1 className="text-2xl font-bold text-slate-900 mb-2">Unable to Access</h1>
          <p className="text-slate-600">{error}</p>
        </div>
      </div>
    );
  }

  if (submitted) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center p-4">
        <div className="max-w-md w-full bg-white rounded-lg shadow-lg p-8 text-center">
          <CheckCircle className="h-16 w-16 text-emerald-500 mx-auto mb-4" />
          <h1 className="text-2xl font-bold text-slate-900 mb-2">Comment Submitted!</h1>
          <p className="text-slate-600 mb-4">
            Thank you for your feedback on SI {siData?.si?.si_number}. 
            Your comment has been recorded and will be reviewed by the team.
          </p>
          <p className="text-sm text-slate-400">You can close this window.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50 py-8 px-4">
      <div className="max-w-3xl mx-auto">
        {/* Header */}
        <div className="bg-slate-900 text-white rounded-t-lg p-6">
          <div className="flex items-center gap-3 mb-2">
            <FileText className="h-6 w-6" />
            <span className="text-sm uppercase tracking-wider opacity-80">Internal Comment Request</span>
          </div>
          <h1 className="text-2xl font-bold font-barlow">
            Site Instruction {siData?.si?.si_number}
          </h1>
          <p className="text-slate-300 mt-1">{siData?.project_name}</p>
        </div>

        {/* SI Details Card */}
        <div className="bg-white border border-slate-200 shadow-sm p-6">
          <h2 className="font-semibold text-lg text-slate-900 mb-4">{siData?.si?.si_title}</h2>
          
          <div className="grid grid-cols-2 gap-4 mb-4 text-sm">
            <div className="flex items-center gap-2 text-slate-600">
              <Building2 className="h-4 w-4" />
              <span>Discipline: {siData?.si?.discipline}</span>
            </div>
            {siData?.si?.issue_date && (
              <div className="flex items-center gap-2 text-slate-600">
                <Calendar className="h-4 w-4" />
                <span>Issue Date: {siData?.si?.issue_date}</span>
              </div>
            )}
            {siData?.si?.location_area && (
              <div className="flex items-center gap-2 text-slate-600">
                <MapPin className="h-4 w-4" />
                <span>Location: {siData?.si?.location_area}</span>
              </div>
            )}
          </div>

          {siData?.si?.si_description && (
            <div className="bg-slate-50 rounded p-4 text-sm text-slate-700 whitespace-pre-wrap">
              {siData?.si?.si_description}
            </div>
          )}

          {siData?.si?.drawings_referenced?.length > 0 && (
            <div className="mt-4">
              <p className="text-xs font-semibold uppercase text-slate-500 mb-2">Referenced Drawings</p>
              <div className="flex flex-wrap gap-2">
                {siData.si.drawings_referenced.map((d, i) => (
                  <Badge key={i} variant="outline" className="text-xs">
                    {d.drawing_number} Rev {d.revision}
                  </Badge>
                ))}
              </div>
            </div>
          )}
        </div>

        {/* Comment Form */}
        <div className="bg-white border border-t-0 border-slate-200 shadow-sm rounded-b-lg p-6">
          <div className="flex items-center gap-2 mb-4">
            <Clock className="h-4 w-4 text-amber-600" />
            <p className="text-sm text-slate-600">
              Commenting as <span className="font-semibold">{siData?.recipient_name}</span>
            </p>
          </div>

          <div className="space-y-6">
            {/* Comment Text */}
            <div>
              <Label className="text-sm font-semibold text-slate-700 mb-2 block">
                Your Comment <span className="text-red-500">*</span>
              </Label>
              <Textarea
                value={formData.comment_text}
                onChange={(e) => setFormData({ ...formData, comment_text: e.target.value })}
                placeholder="Please provide your feedback, concerns, or observations about this Site Instruction..."
                rows={5}
                className="rounded-sm"
                data-testid="comment-text-input"
              />
            </div>

            {/* Tags */}
            <div>
              <Label className="text-sm font-semibold text-slate-700 mb-2 block">
                Relevant Categories (Optional)
              </Label>
              <div className="flex flex-wrap gap-2">
                {COMMENT_TAGS.map(tag => (
                  <Badge
                    key={tag}
                    className={`cursor-pointer transition-colors ${
                      formData.tags.includes(tag)
                        ? 'bg-slate-900 text-white hover:bg-slate-800'
                        : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
                    }`}
                    onClick={() => toggleTag(tag)}
                  >
                    {tag}
                  </Badge>
                ))}
              </div>
            </div>

            {/* Impact Assessments */}
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label className="text-sm font-semibold text-slate-700 mb-2 block">
                  Cost Impact Expected?
                </Label>
                <Select 
                  value={formData.cost_impact} 
                  onValueChange={(v) => setFormData({ ...formData, cost_impact: v })}
                >
                  <SelectTrigger className="rounded-sm">
                    <SelectValue placeholder="Select..." />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="Yes">Yes - Cost impact expected</SelectItem>
                    <SelectItem value="No">No - No cost impact</SelectItem>
                    <SelectItem value="Unknown">Unknown - Need more info</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-sm font-semibold text-slate-700 mb-2 block">
                  Time Impact Expected?
                </Label>
                <Select 
                  value={formData.time_impact} 
                  onValueChange={(v) => setFormData({ ...formData, time_impact: v })}
                >
                  <SelectTrigger className="rounded-sm">
                    <SelectValue placeholder="Select..." />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="Yes">Yes - Schedule impact expected</SelectItem>
                    <SelectItem value="No">No - No schedule impact</SelectItem>
                    <SelectItem value="Unknown">Unknown - Need more info</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            {/* Recommended Action */}
            <div>
              <Label className="text-sm font-semibold text-slate-700 mb-2 block">
                Recommended Action (Optional)
              </Label>
              <Textarea
                value={formData.recommended_action}
                onChange={(e) => setFormData({ ...formData, recommended_action: e.target.value })}
                placeholder="What action do you recommend regarding this SI? (e.g., Accept, Request Clarification, Reject with Reason)"
                rows={2}
                className="rounded-sm"
              />
            </div>

            {/* Submit */}
            <Button
              onClick={handleSubmit}
              disabled={submitting || !formData.comment_text.trim()}
              className="w-full bg-slate-900 text-white hover:bg-slate-800 rounded-sm py-6 text-base"
              data-testid="submit-comment-button"
            >
              {submitting ? (
                <>
                  <Loader2 className="h-5 w-5 mr-2 animate-spin" />
                  Submitting...
                </>
              ) : (
                <>
                  <Send className="h-5 w-5 mr-2" />
                  Submit Comment
                </>
              )}
            </Button>

            <p className="text-xs text-slate-400 text-center">
              Link expires: {siData?.expires_at ? new Date(siData.expires_at).toLocaleDateString() : 'N/A'}
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}
